// Consultation/ConsultationActions.tsx

import React, {useContext, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {axios} from 'Auth/axiosConfig';
import {useErrorProcessing} from 'Core/components/ErrorProvider';
import {AuthContext, AuthContextType} from 'Auth/AuthContext';
import {MenuItem} from '@mui/material';
import OptionsMenu from 'Core/components/elements/OptionsMenu';
import Button from 'Core/components/elements/Button/Button';
import {useTheme} from 'Theme/ThemeContext';
import {Message} from 'Core/components/Message';
import {useProfile} from 'User/ProfileContext';
import pprint from 'Utils/pprint';
import moment from 'moment';
import {IConsultation} from 'Types/psychology/consultation';
import {FC as FCC, FR} from 'WideLayout/Layouts';
import ConsultationRescheduleForm from "Consultation/ConsultationRescheduleForm";
import ConsultationFeedbackForm from "../ConsultationFeedback/ConsultationFeedbackForm";

interface ConsultationActionsProps {
    consultation: IConsultation;
    extended?: boolean;
    cls?: string;
    clsFeedbackForm?: string;
    onConsultationCancel: (updatedConsultation: IConsultation) => void;
    onConsultationReschedule: (updatedConsultation: IConsultation) => void;
    onConsultationUpdate?: (updatedConsultation: IConsultation) => void;
    setLoading: (loading: boolean) => void;
}

const ConsultationActions: React.FC<ConsultationActionsProps> = (
    {
        consultation,
        cls,
        clsFeedbackForm,
        extended = false,
        onConsultationCancel,
        onConsultationReschedule,
        onConsultationUpdate,
        setLoading,
    }) => {
    const navigate = useNavigate();
    const {byResponse} = useErrorProcessing();
    const {user} = useContext(AuthContext) as AuthContextType;
    const {plt} = useTheme();
    const {selectedProfile} = useProfile();

    const {
        id,
        psychologists,
        date,
        status,
        conference,
        order,
        config,
        is_reservation,
        feedbacks
    } = consultation;

    const userIsPsychologist = (selectedProfile === 'psychologist');
    const userIsClient = user?.client;
    const userIsStaff = user?.is_staff;

    const existingFeedback = feedbacks && feedbacks.find(fb => fb.user === user?.id);

    const consultationDate = date ? new Date(date) : null;
    const dateInPast = consultationDate && (consultationDate.getTime() < (Date.now() - 20 * 60 * 1000));
    const hasFeedbackFromUser = !!existingFeedback;
    const isCompletedForUser = (status === 'PAID' && dateInPast && hasFeedbackFromUser);
    const waitingForFeedback = (status === 'PAID' && dateInPast && !hasFeedbackFromUser);

    const canLeaveOrEditFeedback = () => {
        if (status !== 'PAID') return false;
        if (!dateInPast) return false;
        return (userIsClient || userIsPsychologist || userIsStaff);
    };

    const canShowBtnToConference = (consultationDate: string, duration: number): boolean => {
        if (!consultationDate || !duration) return false;
        const preDelay = 15;
        const postDelay = 20;
        const now = new Date();
        const conferenceStart = new Date(consultationDate);
        const conferenceEnd = new Date(
            conferenceStart.getTime() + (duration + postDelay) * 60 * 1000
        );
        return now >= new Date(conferenceStart.getTime() - preDelay * 60 * 1000) && now <= conferenceEnd;
    };

    const getTimeUntil = (dateStr: string) => {
        if (!dateStr) return '';
        const now = new Date();
        const targetDate = new Date(dateStr);
        const diffMs = targetDate.getTime() - now.getTime();
        if (diffMs <= 0) return 'Дата уже прошла';
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
        if (diffHours > 0) return `Начало через ${diffHours} часов`;
        else if (diffMinutes > 0) return `Начало через ${diffMinutes} минут`;
        else return 'Начнется менее чем через минуту';
    };

    const canReschedule = (userIsStaff || userIsClient || userIsPsychologist) && date && status === 'PAID';

    const handleJoinConference = () => {
        if (conference && conference.name) {
            navigate(`/conference/${id}`);
        } else {
            Message.error('Комната не найдена');
        }
    };

    const handlePay = () => {
        setLoading(true);
        if (is_reservation) {
            axios.post(`/api/v1/consultations/reservation/${id}/pay-by-balance/`)
                .then(response => {
                    pprint('Reservation pay by balance', response.data);
                    if (response.data.need_order) {
                        Message.info('На балансе нет доступных консультаций');
                        if (psychologists.length === 1) {
                            navigate(`/psychologists/${psychologists[0].id}/?reservation_paid=${id}`);
                        } else if (psychologists.length > 1) {
                            navigate(`/psychologists/multiple-selection/?reservation_paid=${id}`);
                        } else {
                            navigate(`/psychologists/?reservation_paid=${id}`);
                        }
                    } else {
                        if (onConsultationUpdate) onConsultationUpdate(response.data);
                        Message.success('Оплата с баланса успешно проведена');
                    }
                })
                .catch(error => byResponse(error))
                .finally(() => setLoading(false));
        } else if (order?.payment) {
            window.location.href = order.payment.payment_url;
            setLoading(false);
        } else {
            Message.error('Ссылка для оплаты не найдена');
            setLoading(false);
        }
    };

    const handleGoToOrder = () => {
        if (order) navigate(`/orders/${order.id}`);
    };

    const handleCancelConsultation = () => {
        setLoading(true);
        axios
            .post(`/api/v1/consultations/${id}/cancel/`)
            .then(response => {
                pprint('Consultation cancel', response.data);
                onConsultationCancel(response.data);
                Message.success('Консультация успешно отменена');
            })
            .catch(error => byResponse(error))
            .finally(() => setLoading(false));
    };

    // Reschedule
    const [rescheduleMode, setRescheduleMode] = useState(false);
    const [rescheduleLoading, setRescheduleLoading] = useState<boolean>(false);

    const handleOpenReschedule = () => {
        setRescheduleMode(true);
    };

    const handleConfirmReschedule = (newDate: string, reason: string) => {
        setRescheduleLoading(true);
        axios.post(`/api/v1/consultations/${id}/reschedule/`, {
            new_date: newDate,
            reason: reason.trim() || undefined
        }).then(response => {
            pprint('Consultation reschedule', response.data);
            onConsultationReschedule(response.data);
            Message.success(`Консультация успешно перенесена на ${moment(newDate).format('DD.MM.YYYY HH:mm')}`);
            setRescheduleMode(false);
        }).catch(e => byResponse(e)
        ).finally(() => setRescheduleLoading(false));
    };

    const handleCancelReschedule = () => {
        if (!rescheduleLoading) {
            setRescheduleMode(false);
        }
    };

    // Feedback
    const [feedbackMode, setFeedbackMode] = useState(false);
    const [feedbackLoading, setFeedbackLoading] = useState(false);

    const handleOpenFeedback = () => {
        setFeedbackMode(true);
    };

    const handleConfirmFeedback = async (
        consultationRating?: number | null,
        connectionRating?: number | null,
        consultationFeedback?: string,
        connectionFeedback?: string,
        dontWantToRate?: boolean,
        skills?: number[] | null,
    ) => {
        setFeedbackLoading(true);
        try {
            const method = existingFeedback ? 'PATCH' : 'POST';
            const response = await axios({
                method: method,
                url: `/api/v1/consultations/${id}/feedback/`,
                data: {
                    rating_consultation: consultationRating,
                    rating_connection: connectionRating,
                    feedback_consultation: consultationFeedback || undefined,
                    feedback_connection: connectionFeedback || undefined,
                    dont_want_to_rate: dontWantToRate,
                    skills: skills || [],
                }
            });
            pprint('Consultation feedback created/updated', response.data);
            onConsultationUpdate && onConsultationUpdate(response.data);
            Message.success(existingFeedback ? 'Отзыв обновлен.' : 'Отзыв успешно оставлен.');
            setFeedbackMode(false);
        } catch (error: any) {
            byResponse(error);
        } finally {
            setFeedbackLoading(false);
        }
    };

    const handleCancelFeedback = () => {
        if (!feedbackLoading) {
            setFeedbackMode(false);
        }
    };

    const acts = (() => {
        const arr: Array<{
            label: string;
            onClick: () => void;
            style?: React.CSSProperties;
            disabled?: boolean;
        }> = [];

        if (!waitingForFeedback) {
            if (userIsClient && !userIsPsychologist) {
                switch (status) {
                    case 'NEW':
                        arr.push({
                            label: 'Оплатить',
                            onClick: handlePay,
                            style: {backgroundColor: plt.colors.secondary.main},
                        });
                        arr.push({
                            label: 'Отменить',
                            onClick: handleCancelConsultation,
                            style: {backgroundColor: plt.colors.error.main},
                        });
                        break;
                    case 'CANCELLED':
                        arr.push({
                            label: 'Повторить',
                            onClick: () =>
                                navigate(
                                    `/psychologists/${consultation.psychologists[0].id}/?config=${consultation.config.id}`
                                ),
                            style: {
                                backgroundColor: plt.colors.secondary.main,
                            },
                        });
                        break;
                    case 'PAID':
                        if (date) {
                            if (canShowBtnToConference(date, config.duration.value) && conference) {
                                arr.push({
                                    label: 'В конференцию',
                                    onClick: handleJoinConference,
                                    style: {
                                        backgroundColor: plt.colors.secondary.main,
                                        color: '#fffe',
                                        fontWeight: 'bold',
                                    },
                                });
                            } else {
                                arr.push({
                                    onClick: () => {
                                    },
                                    label: getTimeUntil(date),
                                    disabled: true,
                                });
                            }
                        }
                        if (isCompletedForUser && consultation.psychologists.length > 0) {
                            arr.push({
                                label: 'Повторить',
                                onClick: () =>
                                    navigate(`/psychologists/${consultation.psychologists[0].id}/?config=${consultation.config.id}`),
                                style: {backgroundColor: plt.info.main},
                            });
                        }
                        break;
                    default:
                        break;
                }
            }

            if (order && !userIsPsychologist) {
                arr.push({
                    label: 'Посмотреть заказ',
                    onClick: handleGoToOrder,
                    style: {
                        backgroundColor: plt.colors.secondary.light,
                        color: plt.text.contrast,
                    },
                });
            }

            if (canReschedule && !isCompletedForUser) {
                arr.push({
                    label: 'Перенести',
                    onClick: handleOpenReschedule,
                    style: {
                        backgroundColor: plt.colors.secondary.light,
                        color: plt.text.contrast,
                    },
                });
            }

            if (date && status === 'PAID' && !isCompletedForUser) {
                arr.push({
                    label: 'Отменить',
                    onClick: handleCancelConsultation,
                    style: {
                        backgroundColor: plt.colors.error.light,
                        color: plt.text.contrast,
                    },
                });
            }

            if (status === 'PAID' && isCompletedForUser && !extended) {
                arr.push({
                    label: 'Повторить',
                    onClick: () =>
                        navigate(
                            `/psychologists/${consultation.psychologists[0].id}/?config=${consultation.config.id}`
                        ),
                    style: {
                        backgroundColor: plt.colors.secondary.main,
                    },
                });
            }
        }

        return arr;
    })();

    // Кнопка отзыва
    // Если есть feedback -> редактировать оценку в меню, а Повторить - вне меню
    // Если нет feedback -> Оценить вне меню, Повторить как было
    let feedbackAct = null;
    let repeatAction = null;
    let feedbackBtn = null;

    // Ищем повторить в acts
    const repeatIndex = acts.findIndex(a => a.label === 'Повторить');
    if (repeatIndex >= 0) {
        repeatAction = acts[repeatIndex];
        acts.splice(repeatIndex, 1);
    }

    if (canLeaveOrEditFeedback()) {
        if (existingFeedback) {
            // Есть отзыв -> "Редактировать оценку" в меню
            feedbackAct = {
                label: 'Редактировать оценку',
                onClick: handleOpenFeedback,
                style: {
                    backgroundColor: plt.colors.secondary.light,
                    color: '#fff',
                },
            };
            acts.push(feedbackAct);

        } else {
            // Нет отзыва -> Оценить вне меню
            feedbackBtn = (
                <Button
                    className="h-min px-2 pt-6px"
                    style={{
                        backgroundColor: plt.colors.secondary.main,
                        color: '#fff',
                        fontWeight: 'bold',
                    }}
                    onClick={handleOpenFeedback}
                    size={'small'}
                >
                    Оценить
                </Button>
            );
        }
    }

    return (
        <FR cls={cls}>
            {rescheduleMode ? (
                <ConsultationRescheduleForm
                    cls={`p-2`}
                    consultation={consultation}
                    onConfirm={handleConfirmReschedule}
                    onCancel={handleCancelReschedule}
                    loading={rescheduleLoading}
                />
            ) : feedbackMode ? (
                <ConsultationFeedbackForm
                    cls={clsFeedbackForm}
                    onConfirm={handleConfirmFeedback}
                    onCancel={handleCancelFeedback}
                    loading={feedbackLoading}
                    defaultConsultationRating={existingFeedback?.dont_want_to_rate ? null : existingFeedback?.rating_consultation || null}
                    defaultConnectionRating={existingFeedback?.dont_want_to_rate ? null : existingFeedback?.rating_connection || null}
                    defaultConsultationFeedback={existingFeedback?.feedback_consultation || ''}
                    defaultConnectionFeedback={existingFeedback?.feedback_connection || ''}
                    defaultDontWantToRate={existingFeedback?.dont_want_to_rate || false}
                    // Передаем только массив айдишников
                    defaultSkills={
                        existingFeedback?.client_request?.psychologist_skills
                            ? existingFeedback.client_request.psychologist_skills.map(s => s.id)
                            : []
                    }
                />
            ) : (
                extended
                    ? <FCC g={1} maxW={250} mt={1}>
                        {repeatAction && (
                            <Button
                                className="h-min px-2 pt-5px"
                                style={repeatAction.style}
                                onClick={repeatAction.onClick}
                                size="small"
                            >
                                {repeatAction.label}
                            </Button>
                        )}

                        {acts.map((action, index) =>
                            action.disabled ? (
                                <FCC key={index} g={0}>
                                    <span>{action.label}</span>
                                    <span className="text-xs">
                                            Кнопка подключения появится за&nbsp;15&nbsp;минут до&nbsp;начала
                                        </span>
                                </FCC>
                            ) : (
                                <Button
                                    key={index}
                                    className="h-min px-2 pt-5px"
                                    style={action.style}
                                    onClick={action.onClick}
                                    size="small"
                                >
                                    {action.label}
                                </Button>
                            )
                        )}

                        {feedbackBtn}
                    </FCC>
                    : (
                        (acts.length > 0 || feedbackBtn || repeatAction)
                            ? <FR g={1}>
                                {date && canShowBtnToConference(date, config.duration.value)
                                    && status === 'PAID' && conference &&
                                    <Button
                                        className="h-min px-2 pt-6px"
                                        style={{
                                            backgroundColor: plt.colors.secondary.main,
                                            color: '#fff',
                                            fontWeight: 'bold',
                                        }}
                                        onClick={handleJoinConference}
                                        size={'small'}
                                    >
                                        В конференцию
                                    </Button>}
                                {repeatAction && (
                                    <Button
                                        className="h-min px-2 pt-5px"
                                        style={repeatAction.style}
                                        onClick={repeatAction.onClick}
                                        size={'small'}
                                    >
                                        {repeatAction.label}
                                    </Button>
                                )}
                                {(!existingFeedback && feedbackBtn) && feedbackBtn}
                                {acts.length > 0 && (
                                    <OptionsMenu iconClassName="p-1">
                                        {acts.map((action, index) => (
                                            <MenuItem
                                                key={index}
                                                onClick={action.onClick}
                                                disabled={action.disabled}>
                                                {action.label}
                                            </MenuItem>
                                        ))}
                                    </OptionsMenu>
                                )}
                            </FR>
                            : null
                    )
            )}
        </FR>
    );
};

export default ConsultationActions;
